// Zmodyfikowany fragment komponentu DocumentDetails.js z dodanym przyciskiem eksportu
import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    CircularProgress,
    Button,
    Alert,
    Chip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Stack
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Description as DescriptionIcon,
    Receipt as ReceiptIcon,
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    FileDownload as DownloadIcon
} from '@mui/icons-material';
import DocumentPreview from './DocumentPreview';
import ExportCSVButton from './ExportCSVButton'; // Zaimportuj nowy komponent

const API_URL = process.env.REACT_APP_API_URL || '';

const DocumentDetails = () => {
    const { id } = useParams();
    const [documentData, setDocumentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDocument = async () => {
            try {
                const response = await fetch(`${API_URL}/api/expenses/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch document details');
                }

                const data = await response.json();

                // Parsowanie danych JSON jeśli są dostępne
                if (data.ai_extracted_data) {
                    data.parsedData = typeof data.ai_extracted_data === 'string'
                        ? JSON.parse(data.ai_extracted_data)
                        : data.ai_extracted_data;
                }

                setDocumentData(data);
            } catch (err) {
                console.error('Error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDocument();
    }, [id]);

    if (loading) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 4 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!documentData) {
        return (
            <Container>
                <Alert severity="warning" sx={{ mt: 4 }}>
                    Nie znaleziono dokumentu
                </Alert>
            </Container>
        );
    }

    const parsedData = documentData.parsedData || {};

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 6 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        startIcon={<ArrowBackIcon />}
                    >
                        Powrót do listy
                    </Button>
                    <Box>
                        {/* Dodajemy przycisk eksportu CSV */}
                        <ExportCSVButton expenseId={id} variant="outlined" sx={{ mr: 1 }} />

                        <Chip
                            label={parsedData.documentType === 'receipt' ? 'Paragon' : 'Faktura'}
                            color="primary"
                            icon={<ReceiptIcon />}
                        />
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Panel z podglądem dokumentu */}
                    <Grid item xs={12} md={6}>
                        <DocumentPreview
                            expenseId={id}
                            extractedData={documentData}
                        />
                    </Grid>

                    {/* Panel ze szczegółami */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            {/* Podstawowe informacje */}
                            <Paper sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">
                                        Podstawowe informacje
                                    </Typography>

                                    {/* Opcjonalnie: przycisk edycji dla przyszłej implementacji */}
                                    <Button
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => alert('Funkcja edycji będzie dostępna wkrótce')}
                                    >
                                        Edytuj
                                    </Button>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Numer dokumentu</Typography>
                                        <Typography>{parsedData.documentNumber || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Data wystawienia</Typography>
                                        <Typography>{parsedData.dates?.issueDate || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Data sprzedaży</Typography>
                                        <Typography>{parsedData.dates?.saleDate || '-'}</Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Kwoty */}
                            <Paper sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">
                                        Kwoty
                                    </Typography>

                                    {/* Informacje o metodzie przetwarzania OCR (nowe) */}
                                    {documentData.processing_method && (
                                        <Chip
                                            size="small"
                                            label={documentData.processing_method}
                                            color="info"
                                        />
                                    )}
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Kwota netto</Typography>
                                        <Typography>
                                            {parsedData.amounts?.net?.toFixed(2) || '-'} {parsedData.amounts?.currency}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Kwota VAT</Typography>
                                        <Typography>
                                            {parsedData.amounts?.tax?.toFixed(2) || '-'} {parsedData.amounts?.currency}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Kwota brutto</Typography>
                                        <Typography variant="h6" color="primary">
                                            {parsedData.amounts?.gross?.toFixed(2) || '-'} {parsedData.amounts?.currency}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Metoda płatności</Typography>
                                        <Chip
                                            label={
                                                parsedData.payment?.method === 'transfer' ? 'Przelew' :
                                                    parsedData.payment?.method === 'cash' ? 'Gotówka' :
                                                        parsedData.payment?.method === 'card' ? 'Karta' : '-'
                                            }
                                            color="default"
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Kontrahenci */}
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Kontrahenci
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">Sprzedawca</Typography>
                                            <Typography>{parsedData.parties?.seller?.name || '-'}</Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                NIP: {parsedData.parties?.seller?.taxId || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                {parsedData.parties?.seller?.address || '-'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">Nabywca</Typography>
                                            <Typography>{parsedData.parties?.buyer?.name || '-'}</Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                NIP: {parsedData.parties?.buyer?.taxId || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                {parsedData.parties?.buyer?.address || '-'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Pozycje na fakturze */}
                            {parsedData.lineItems && parsedData.lineItems.length > 0 && (
                                <Paper sx={{ p: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Pozycje na dokumencie
                                    </Typography>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Opis</TableCell>
                                                <TableCell align="right">Ilość</TableCell>
                                                <TableCell align="right">Cena jedn.</TableCell>
                                                <TableCell align="right">VAT</TableCell>
                                                <TableCell align="right">Wartość brutto</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {parsedData.lineItems.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{item.description}</TableCell>
                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                    <TableCell align="right">
                                                        {item.unitPrice?.toFixed(2)}
                                                    </TableCell>
                                                    <TableCell align="right">{item.taxRate}%</TableCell>
                                                    <TableCell align="right">
                                                        {item.grossAmount?.toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            )}

                            {/* Informacje o przetwarzaniu OCR (nowe) */}
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Informacje o przetwarzaniu
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2">Liczba stron</Typography>
                                        <Typography>{documentData.page_count || 1}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2">Metoda przetwarzania</Typography>
                                        <Typography>{documentData.processing_method || 'standard'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2">Data przetworzenia</Typography>
                                        <Typography>
                                            {new Date(documentData.updated_at).toLocaleDateString('pl-PL')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Tekst OCR */}
                            {documentData.document_text && (
                                <Paper sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6">
                                            Tekst rozpoznany (OCR)
                                        </Typography>

                                        {/* Dodajemy tutaj również przycisk eksportu */}
                                        <ExportCSVButton expenseId={id} size="small" />
                                    </Box>

                                    <Box sx={{
                                        backgroundColor: '#f5f5f5',
                                        p: 2,
                                        borderRadius: 1,
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                        maxHeight: '200px',
                                        overflow: 'auto'
                                    }}>
                                        {documentData.document_text}
                                    </Box>
                                </Paper>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default DocumentDetails;